import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCVStore } from '@/store/cv.store';
import { useCoverLetterStore } from '@/store/cover-letter.store';
import { DocumentCard, DocumentsEmptyState } from '@/components/documents';
import { CoverLetterPreview } from '@/components/cover-letter';
import { DailyLimitBanner } from '@/components/freemium';
import { ExtensionPrompt } from '@/components/ExtensionPrompt';
import { Button, useToast } from '@/components/common';
import { cvService } from '@/services/cv.service';
import { useProfileStore } from '@/store/profile.store';
import { useAuth } from '@/hooks';
import { InlineLoader } from '@shared/ui/loading';
import '@/styles/documents.css';

type TabType = 'cvs' | 'coverLetters';
type CVTemplate = 'professional' | 'modern' | 'twoColumn' | 'harvard';

const CV_TEMPLATES: Array<{ value: CVTemplate; label: string }> = [
  { value: 'professional', label: 'Professional' },
  { value: 'modern', label: 'Modern' },
  { value: 'twoColumn', label: 'Two Column' },
  { value: 'harvard', label: 'Harvard' },
];

export default function DocumentsPage() {
  const { generatedCVs, removeCV, addGeneratedCV, fetchFromBackend: fetchCVs, hasFetchedFromBackend: hasFetchedCVs } = useCVStore();
  const { coverLetters, removeCoverLetter, fetchFromBackend: fetchCoverLetters, hasFetchedFromBackend: hasFetchedCL } = useCoverLetterStore();
  const { profile } = useProfileStore();
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('cvs');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CVTemplate>('professional');
  const [showGenerator, setShowGenerator] = useState(false);

  // Fetch CVs and Cover Letters from backend when authenticated (once per session)
  useEffect(() => {
    if (isAuthenticated && !hasFetchedCVs) {
      fetchCVs();
    }
    if (isAuthenticated && !hasFetchedCL) {
      fetchCoverLetters();
    }
  }, [isAuthenticated, hasFetchedCVs, hasFetchedCL, fetchCVs, fetchCoverLetters]);

  /** Check if profile has enough data to generate a meaningful CV */
  const getProfileGaps = (): string[] => {
    if (!profile) return ['profile'];
    const gaps: string[] = [];
    if (!profile.firstName && !profile.lastName) gaps.push('name');
    if (!profile.experience?.length) gaps.push('work experience');
    if (!profile.skills?.length) gaps.push('skills');
    return gaps;
  };

  const handleGenerateCV = async () => {
    if (!profile) {
      toast.error('Please create a profile first.');
      navigate('/app/onboarding');
      return;
    }

    const gaps = getProfileGaps();
    if (gaps.length > 1) {
      toast.error(
        `Your profile needs more data to generate a good CV. Please add: ${gaps.join(', ')}.`
      );
      return;
    }

    setIsGenerating(true);
    try {
      const result = await cvService.generateCV({
        template: selectedTemplate,
      });
      addGeneratedCV(result.cv as any);
      toast.success('CV generated successfully!');
      setShowGenerator(false);
    } catch (error) {
      console.error('CV generation error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate CV. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const result = await cvService.exportCV(id, 'pdf');
      cvService.downloadFile(result.buffer, result.filename);
      toast.success('CV downloaded!');
    } catch (error) {
      // PDF failed — fallback to HTML download from local store
      const cv = generatedCVs.find((c) => c.id === id);
      if (cv?.rendered) {
        const blob = new Blob([cv.rendered], { type: 'text/html' });
        cvService.downloadFile(blob, `cv-${id}.html`);
        toast.info('Downloaded as HTML (PDF unavailable)');
      } else {
        console.error('Failed to download document:', error);
        toast.error('Failed to download CV');
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      removeCV(id);
      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const cvs = generatedCVs;

  const handleDeleteCoverLetter = (id: string) => {
    if (!confirm('Are you sure you want to delete this cover letter?')) return;
    removeCoverLetter(id);
    toast.success('Cover letter deleted');
  };

  const renderContent = () => {
    if (activeTab === 'cvs') {
      if (cvs.length === 0) {
        return <DocumentsEmptyState type="cv" />;
      }

      return (
        <div className="documents-grid">
          {cvs.map((cv) => (
            <DocumentCard
              key={cv.id}
              document={cv}
              type="cv"
              onDownload={handleDownload}
              onDelete={handleDelete}
            />
          ))}
        </div>
      );
    }

    // Cover Letters tab
    if (coverLetters.length === 0) {
      return <DocumentsEmptyState type="coverLetter" />;
    }

    return (
      <div className="documents-cover-letters">
        {coverLetters.map((cl) => (
          <div key={cl.id} className="documents-cover-letter-card">
            <div className="documents-cover-letter-card__header">
              <div>
                <h4 className="documents-cover-letter-card__title">
                  Cover Letter {cl.jobTitle ? `for ${cl.jobTitle}` : ''}
                </h4>
                {cl.company && (
                  <p className="documents-cover-letter-card__company">{cl.company}</p>
                )}
                <p className="documents-cover-letter-card__date">
                  {new Date(cl.generatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </p>
              </div>
              <button
                onClick={() => handleDeleteCoverLetter(cl.id)}
                className="documents-cover-letter-card__delete"
                title="Delete"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
            <CoverLetterPreview
              content={cl.content}
              letterText={cl.letterText}
              template={cl.template}
              wordCount={cl.wordCount}
              atsKeywordCoverage={cl.atsKeywordCoverage}
            />
          </div>
        ))}
      </div>
    );
  };

  const gaps = getProfileGaps();

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Daily limit banner */}
      <DailyLimitBanner />

      {/* Page title + Generate button */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">My Documents</h1>
          <p className="mt-1 text-text-secondary">
            CVs and cover letters generated from your profile or pipeline jobs.
          </p>
        </div>
        <Button onClick={() => setShowGenerator(!showGenerator)} disabled={isGenerating}>
          {showGenerator ? 'Close' : 'Generate CV from Profile'}
        </Button>
      </div>

      {/* Quick CV Generator */}
      {showGenerator && (
        <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
          <h3 className="font-medium text-text-primary">Generate a CV from your profile</h3>
          <p className="text-sm text-text-secondary">
            Creates a general CV based on your profile data. For job-specific CVs, go to a job in
            your{' '}
            <button
              onClick={() => navigate('/app/pipeline')}
              className="text-primary underline hover:no-underline"
            >
              Pipeline
            </button>{' '}
            and generate from there.
          </p>

          {/* Profile gaps warning */}
          {gaps.length > 0 && (
            <div className="bg-warning-soft border border-warning rounded-lg p-3">
              <p className="text-sm text-text-primary">
                <strong>Tip:</strong> Add <strong>{gaps.join(', ')}</strong> to your{' '}
                <button
                  onClick={() => navigate('/app/profile')}
                  className="text-primary underline hover:no-underline"
                >
                  profile
                </button>{' '}
                for a better CV.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {CV_TEMPLATES.map((t) => (
              <button
                key={t.value}
                onClick={() => setSelectedTemplate(t.value)}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                  selectedTemplate === t.value
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-border text-text-primary hover:border-primary-soft'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {isGenerating ? (
            <div className="flex items-center gap-3 py-2">
              <InlineLoader size="md" />
              <p className="text-sm text-text-secondary">
                Generating your CV... This may take up to 1 minute.
              </p>
            </div>
          ) : (
            <Button onClick={handleGenerateCV}>
              Generate {selectedTemplate} CV
            </Button>
          )}
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setActiveTab('cvs')}
          className={`
            px-4 py-3 font-medium text-sm border-b-2 transition-colors
            ${
              activeTab === 'cvs'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
            }
          `}
        >
          CVs
          {cvs.length > 0 && (
            <span
              className={`
                ml-2 px-2 py-0.5 rounded-full text-xs font-medium
                ${activeTab === 'cvs' ? 'bg-primary text-white' : 'bg-surface text-text-secondary'}
              `}
            >
              {cvs.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('coverLetters')}
          className={`
            px-4 py-3 font-medium text-sm border-b-2 transition-colors
            ${
              activeTab === 'coverLetters'
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
            }
          `}
        >
          Cover Letters
          {coverLetters.length > 0 && (
            <span
              className={`
                ml-2 px-2 py-0.5 rounded-full text-xs font-medium
                ${activeTab === 'coverLetters' ? 'bg-primary text-white' : 'bg-surface text-text-secondary'}
              `}
            >
              {coverLetters.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {renderContent()}

      {/* Extension prompt — auto-fill applications */}
      {cvs.length > 0 && (
        <div className="mt-6">
          <ExtensionPrompt variant="post-cv" />
        </div>
      )}
    </div>
  );
}
